import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiHome, FiShield, FiLinkedin, FiTwitter, FiGithub, FiMail } from 'react-icons/fi';
import PageHeader from '../components/layout/PageHeader';
import { pageDescriptions } from '../data/pageDescriptions';

const TeamPage = () => {
  const [hoveredFounder, setHoveredFounder] = useState(null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="w-full py-4 px-6 bg-white shadow-sm">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          <Link to="/" className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors">
            <img src="/logo.png" alt="Resumemo" className="h-8 w-auto" />
            <span className="text-xl font-bold">Resumemo</span>
          </Link>
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/" className="flex items-center space-x-1 text-gray-600 hover:text-primary-600 transition-colors">
              <FiHome className="h-4 w-4" />
              <span>Home</span>
            </Link>
            <Link to="/privacy-policy" className="flex items-center space-x-1 text-gray-600 hover:text-primary-600 transition-colors">
              <FiShield className="h-4 w-4" />
              <span>Privacy</span>
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto mb-12">
          <PageHeader
            title={pageDescriptions['/team'].title}
            description={pageDescriptions['/team'].description}
          />
        </div>
        {/* Meet the Founders Section */}
        <section className="max-w-6xl mx-auto mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet the Founders</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're two builders with a shared mission: make job hunting less painful, more powerful, and deeply personal.
            </p>
          </div>

          {/* Founders Photo Container */}
          <div 
            className="relative max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-2xl cursor-pointer group"
            onMouseEnter={() => setHoveredFounder('both')}
            onMouseLeave={() => setHoveredFounder(null)}
          >
            <img 
              src="/assets/TeamPhoto.jpg" 
              alt="Xixi and Qi - Resumemo Founders" 
              className="w-full h-auto object-cover block transition-transform duration-500 group-hover:scale-105"
              style={{ minHeight: '400px' }}
            />
            
            {/* Bio Panels */}
            <div 
              className={`absolute top-0 bottom-0 left-0 w-1/2 bg-gradient-to-r from-primary-600/90 to-primary-500/90 text-white p-8 flex flex-col justify-center transition-transform duration-700 ease-out md:transform ${
                hoveredFounder === 'both' ? 'md:translate-x-0' : 'md:-translate-x-full'
              }`}
            >
              <div className="space-y-4">
                <h3 className="text-3xl font-bold">Xixi Zhou</h3>
                <p className="text-lg font-medium text-primary-100">Co-founder</p>
                <div className="space-y-2 text-base">
                  <p>👩‍💻 Product · 🎨 Design · ⚙️ Ops · 📣 Marketing</p>
                  <p className="italic text-primary-100 mt-4">
                    "I obsess over every pixel and every pitch."
                  </p>
                </div>
                <div className="flex space-x-3 mt-6">
                  <a href="https://www.linkedin.com/in/xixi-zhou-807476a1/" className="text-white hover:text-primary-200 transition-colors">
                    <FiLinkedin className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>

            <div 
              className={`absolute top-0 bottom-0 right-0 w-1/2 bg-gradient-to-l from-blue-600/90 to-cyan-600/90 text-white p-8 flex flex-col justify-center transition-transform duration-700 ease-out md:transform ${
                hoveredFounder === 'both' ? 'md:translate-x-0' : 'md:translate-x-full'
              }`}
            >
              <div className="space-y-4">
                <h3 className="text-3xl font-bold">Qi Zhao</h3>
                <p className="text-lg font-medium text-blue-100">Co-founder</p>
                <div className="space-y-2 text-base">
                  <p>💻 Fullstack · 🧠 Infra · 🤖 Automation</p>
                  <p className="italic text-blue-100 mt-4">
                    "Code is poetry, and I like mine clean and fast."
                  </p>
                </div>
                <div className="flex space-x-3 mt-6">
                  <a href="https://www.linkedin.com/in/qiz216/" className="text-white hover:text-blue-200 transition-colors">
                    <FiLinkedin className="h-5 w-5" />
                  </a>

                </div>
              </div>
            </div>

            {/* Mobile Bio Panels */}
            <div className="md:hidden">
              <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary-600/95 to-primary-500/95 text-white p-6 transition-transform duration-500 ${
                hoveredFounder ? 'translate-y-0' : 'translate-y-full'
              }`}>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-bold mb-2">Xixi Zhou</h3>
                    <p className="text-sm font-medium text-primary-100 mb-2">Co-founder</p>
                    <p className="text-xs">👩‍💻 Product · 🎨 Design · ⚙️ Ops · 📣 Marketing</p>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Qi Zhao</h3>
                    <p className="text-sm font-medium text-blue-100 mb-2">Co-founder</p>
                    <p className="text-xs">💻 Fullstack · 🧠 Infra · 🤖 Automation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tap instruction for mobile */}
          <div className="md:hidden text-center mt-4">
            <p className="text-sm text-gray-500">Tap the photo to learn more about our founders</p>
          </div>
        </section>

        {/* Why We Built Resumemo Section */}
        <section className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Why We Built Resumemo</h2>
            <div className="prose prose-lg prose-gray mx-auto">
              <p className="text-gray-700 text-lg leading-relaxed">
                In a world where job hunting feels overwhelming—new grads facing uncertainty, tech workers facing layoffs—many people are sending out over a hundred applications a week. We built Resumemo to bring structure and hope to that chaos. Our platform helps users track their entire job application journey, making sure every click, every submission, every follow-up counts.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mt-6">
                We believe a precisely tailored resume is the key to landing interviews, so we've made it easy to adapt your resume to every job. And once you get there, our interview mock tool brings together your experience and the company's context, so you're never walking in cold. Resumemo exists to make applying not just easier—but smarter and more successful.
              </p>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <div className="bg-gradient-to-r from-primary-50 to-primary-50 rounded-2xl p-8 border border-primary-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to transform your job search?</h3>
              <p className="text-gray-600 mb-6">Join thousands of job seekers who are landing more interviews with Resumemo.</p>
              <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Get Started Free
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Team Values Section */}
        <section className="max-w-6xl mx-auto mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-lg text-gray-600">The principles that guide everything we build</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Precision</h3>
              <p className="text-gray-600">Every feature is crafted with purpose. We believe in quality over quantity, building tools that truly make a difference.</p>
            </div>

            <div className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🚀</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Innovation</h3>
              <p className="text-gray-600">We're constantly pushing boundaries, using AI and modern tech to solve age-old job search challenges.</p>
            </div>

            <div className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💚</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Empathy</h3>
              <p className="text-gray-600">We've been through the job search struggle ourselves. Every feature is built with genuine care for your success.</p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="w-full py-8 px-6 border-t border-gray-200 bg-white mt-20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="flex items-center space-x-2">
            <img src="/logo.png" alt="Resumemo" className="h-6 w-auto" />
            <span className="text-sm text-gray-500">© 2026 Resumemo. All rights reserved.</span>
          </div>
          <div className="flex space-x-6">
            <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-primary-600 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/" className="text-sm text-gray-500 hover:text-primary-600 transition-colors">
              Terms of Service
            </Link>
            <Link to="/contact" className="text-sm text-gray-500 hover:text-primary-600 transition-colors">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TeamPage;